{% block sellops_analytics_page %}
<sw-page class="sellops-analytics-page">
    {% block sellops_analytics_smart_bar_header %}
    <template #smart-bar-header>
        <h2>
            {{ $tc('sellops.analytics.title') }}
        </h2>
    </template>
    {% endblock %}

    {% block sellops_analytics_smart_bar_actions %}
    <template #smart-bar-actions>
        <div class="sellops-analytics__filter-bar">
            <!-- Preset selector -->
            <div class="sellops-analytics__filter-group">
                <label class="sellops-analytics__filter-label">
                    {{ $tc('sellops.analytics.presetLabel') }}
                </label>
                <select
                    class="sellops-analytics__filter-select"
                    :value="quickRange"
                    @change="onPresetChange"
                >
                    <option value="7">
                        {{ $tc('sellops.analytics.last7Days') }}
                    </option>
                    <option value="30">
                        {{ $tc('sellops.analytics.last30Days') }}
                    </option>
                    <option value="90">
                        {{ $tc('sellops.analytics.last90Days') }}
                    </option>
                    <option value="180">
                        {{ $tc('sellops.analytics.last180Days') }}
                    </option>
                    <option value="360">
                        {{ $tc('sellops.analytics.last360Days') }}
                    </option>
                    <option value="custom">
                        {{ $tc('sellops.analytics.custom') }}
                    </option>
                </select>
            </div>
            <!-- From date — always visible; editing it switches to custom -->
            <div class="sellops-analytics__filter-group">
                <label class="sellops-analytics__filter-label">
                    {{ $tc('sellops.analytics.from') }}
                </label>
                <input
                    type="date"
                    class="sellops-analytics__filter-input"
                    :value="dateFrom"
                    @change="onDateFromChange"
                />
            </div>
            <!-- To date — always visible -->
            <div class="sellops-analytics__filter-group">
                <label class="sellops-analytics__filter-label">
                    {{ $tc('sellops.analytics.to') }}
                </label>
                <input
                    type="date"
                    class="sellops-analytics__filter-input"
                    :value="dateTo"
                    @change="onDateToChange"
                />
            </div>
            <sw-button
                variant="primary"
                size="small"
                :isLoading="isLoading"
                @click="loadAnalytics"
            >
                {{ $tc('sellops.analytics.refresh') }}
            </sw-button>
        </div>
    </template>
    {% endblock %}

    {% block sellops_analytics_content %}
    <template #content>
        <sw-loader v-if="isLoading"/>
        <div
            v-else
            class="sellops-analytics__content"
        >
            {% block sellops_quick_nav %}
            <div class="sellops-analytics__nav-grid">
                <!-- Rules card -->
                <div
                    class="sellops-analytics__nav-card"
                    @click="$router.push({ name: 'sellops.upsell.rules' })"
                >
                    <div class="sellops-analytics__nav-card-icon">
                        <sw-icon
                            name="regular-rule"
                            color="#5C67F2"
                            size="22px"
                        />
                    </div>
                    <div>
                        <div class="sellops-analytics__nav-card-title">
                            {{ $tc('sellops.rules.title') }}
                        </div>
                        <div class="sellops-analytics__nav-card-desc">
                            {{ $tc('sellops.analytics.rulesCardDesc') }}
                        </div>
                    </div>
                    <sw-icon
                        name="regular-chevron-right-s"
                        color="#b0b8c9"
                        class="sellops-analytics__nav-card-arrow"
                    />
                </div>
                <!-- Settings card -->
                <div
                    class="sellops-analytics__nav-card"
                    @click="$router.push({ name: 'sellops.upsell.settings' })"
                >
                    <div class="sellops-analytics__nav-card-icon">
                        <sw-icon
                            name="regular-cog"
                            color="#5C67F2"
                            size="22px"
                        />
                    </div>
                    <div>
                        <div class="sellops-analytics__nav-card-title">
                            {{ $tc('sellops.general.settingsMenuLabel') }}
                        </div>
                        <div class="sellops-analytics__nav-card-desc">
                            {{ $tc('sellops.analytics.settingsCardDesc') }}
                        </div>
                    </div>
                    <sw-icon
                        name="regular-chevron-right-s"
                        color="#b0b8c9"
                        class="sellops-analytics__nav-card-arrow"
                    />
                </div>
            </div>
            {% endblock %}

            {% block sellops_kpi_row %}
            <div class="sellops-analytics__kpi-row">
                <!-- Offers Shown -->
                <sw-card :title="$tc('sellops.analytics.totalImpressions')">
                    <div class="sellops-analytics__kpi-value">
                        {{ formatNumber(stats.totalImpressions) }}
                    </div>
                    <div class="sellops-analytics__kpi-label">
                        {{ $tc('sellops.analytics.impressionsHint') }}
                        <br/>
                        <span class="sellops-analytics__kpi-label-sub">
                            {{ $tc('sellops.analytics.impressionsSubHint') }}
                        </span>
                    </div>
                </sw-card>
                <!-- Upsell Orders -->
                <sw-card :title="$tc('sellops.analytics.totalOrders')">
                    <div class="sellops-analytics__kpi-value">
                        {{ formatNumber(stats.totalOrders) }}
                    </div>
                    <div class="sellops-analytics__kpi-label">
                        {{ $tc('sellops.analytics.ordersHint') }}
                    </div>
                </sw-card>
                <!-- Conversion Rate -->
                <sw-card :title="$tc('sellops.analytics.conversionRate')">
                    <div
                        v-if="stats.totalImpressions > 0"
                        class="sellops-analytics__kpi-value sellops-analytics__kpi-value--highlight"
                    >
                        {{ stats.conversionRate }}%
                    </div>
                    <div
                        v-else
                        class="sellops-analytics__kpi-value--empty"
                    >
                        {{ $tc('sellops.analytics.notAvailable') }}
                    </div>
                    <div class="sellops-analytics__kpi-label">
                        {{ $tc('sellops.analytics.conversionHint') }}
                        <br/>
                        <span
                            v-if="stats.totalImpressions === 0"
                            class="sellops-analytics__kpi-label-sub"
                        >
                            {{ $tc('sellops.analytics.conversionSubHint') }}
                        </span>
                    </div>
                </sw-card>
                <!-- Additional Revenue -->
                <sw-card :title="$tc('sellops.analytics.totalRevenue')">
                    <div
                        class="sellops-analytics__kpi-value sellops-analytics__kpi-value--revenue"
                    >
                        {{ formatCurrency(stats.totalRevenue) }}
                    </div>
                    <div class="sellops-analytics__kpi-label">
                        {{ $tc('sellops.analytics.revenueHint') }}
                    </div>
                </sw-card>
            </div>
            {% endblock %}

            {% block sellops_info_banner %}
            <!-- Info banner: shown only while Offers Shown is still 0 -->
            <div
                v-if="stats.totalOrders > 0 && stats.totalImpressions === 0"
                class="sellops-analytics__info-banner"
            >
                <sw-icon
                    name="regular-info-circle"
                    color="#5C67F2"
                    class="sellops-analytics__info-banner-icon"
                />
                <div class="sellops-analytics__info-banner-text">
                    <strong>
                        {{ $tc('sellops.analytics.historicalBannerTitle') }}
                    </strong>
                    <br/>
                    {{ $tc('sellops.analytics.historicalBannerBody') }}
                </div>
            </div>
            {% endblock %}

            {% block sellops_daily_table %}
            <sw-card
                :title="$tc('sellops.analytics.dailyBreakdown')"
                class="sellops-analytics-card"
            >
                <template v-if="stats.daily && stats.daily.length > 0">
                    <!-- ── Pagination controls — TOP of the table ────────────────
                         Two controls: per-page selector + page navigation.
                         Note: `.sellops-pagination` scoped CSS hides sw-pagination's
                         built-in per-page dropdown; we render our own above it. -->
                    <div class="sellops-analytics__pagination-bar">
                        <!-- Left: per-page selector -->
                        <div class="sellops-analytics__per-page-row">
                            <label
                                for="sellops-analytics-per-page"
                                class="sellops-analytics__per-page-label"
                            >
                                {{ $tc('sellops.analytics.rowsPerPage') }}
                            </label>
                            <select
                                id="sellops-analytics-per-page"
                                class="sellops-analytics__per-page-select"
                                :value="presetPerPage"
                                @change="onPerPagePresetChange"
                            >
                                <option value="10">10</option>
                                <option value="15">15</option>
                                <option value="20">20</option>
                                <option value="25">25</option>
                                <option value="30">30</option>
                                <option value="custom">
                                    {{ $tc('sellops.analytics.customOption') }}
                                </option>
                            </select>
                            <!-- Custom number input — visible only when "Custom…" is selected. -->
                            <template v-if="presetPerPage === 'custom'">
                                <input
                                    type="number"
                                    class="sellops-analytics__per-page-custom"
                                    :min="perPageMin"
                                    :max="perPageMax"
                                    step="1"
                                    inputmode="numeric"
                                    :value="customPerPage"
                                    @blur="onCustomPerPageChange"
                                    @keyup.enter="onCustomPerPageChange"
                                    :placeholder="$tc('sellops.analytics.customPerPagePlaceholder')"
                                />
                                <span class="sellops-analytics__per-page-hint">
                                    ({{ perPageMin }}–{{ perPageMax }})
                                </span>
                            </template>
                            <span class="sellops-analytics__showing-range">
                                {{ $tc('sellops.analytics.showingRange', 0, { start: rangeStart, end: rangeEnd, total: stats.dailyTotal || 0 }) }}
                            </span>
                        </div>
                        <!-- Right: page navigation. Only rendered when there's more than one page. -->
                        <sw-pagination
                            v-if="(stats.dailyTotal || 0) > itemsPerPage"
                            class="sellops-pagination"
                            :page="currentPage"
                            :limit="itemsPerPage"
                            :total="stats.dailyTotal || 0"
                            :total-visible="7"
                            :auto-hide="false"
                            @page-change="onPageChange"
                        />
                    </div>
                    <sw-data-grid
                        :dataSource="stats.daily"
                        :columns="columns"
                        :showSelection="false"
                        :showActions="false"
                        :compactMode="false"
                        identifier="sellops-analytics-grid"
                    >
                        <template #column-date="{ item }">
                            {{ formatDate(item.date) }}
                        </template>

                        <template #column-impressions="{ item }">
                            {{ formatNumber(item.impressions) }}
                        </template>

                        <template #column-orders="{ item }">
                            {{ formatNumber(item.orders) }}
                        </template>

                        <template #column-revenue="{ item }">
                            {{ formatCurrency(item.revenue) }}
                        </template>
                    </sw-data-grid>
                </template>

                <template v-else>
                    <div class="sellops-analytics__no-data">
                        <p class="sellops-analytics__no-data-title">
                            {{ $tc('sellops.analytics.noDataInPeriod') }}
                        </p>
                        <p class="sellops-analytics__no-data-hint">
                            {{ $tc('sellops.analytics.noDataInPeriodHint') }}
                        </p>
                    </div>
                </template>
            </sw-card>
            {% endblock %}
        </div>
    </template>
    {% endblock %}
</sw-page>
{% endblock %}